import type { NavigationState, PartialState } from '@react-navigation/native'
import type { AppNavigatorParams } from './types'
import type { StackNavigationProp } from '@react-navigation/stack'

type RootNav = StackNavigationProp<AppNavigatorParams>

type FocusedRouteSnapshot = {
  name: string
  params?: Record<string, unknown>
}

/**
 * Deepest focused route in the tree (walks into nested navigator `state`).
 */
function getFocusedLeafRoute(
  navigationState: NavigationState | PartialState<NavigationState> | undefined
): FocusedRouteSnapshot | undefined {
  if (!navigationState?.routes?.length) {
    return undefined
  }
  const topOfStack = navigationState.routes[navigationState.routes.length - 1]
  if (topOfStack.state) {
    return getFocusedLeafRoute(topOfStack.state as NavigationState)
  }
  return {
    name: String(topOfStack.name),
    params: (topOfStack.params || undefined) as
      | Record<string, unknown>
      | undefined,
  }
}

/**
 * Serializes a route into the `returnPath` string consumed by `goAfterLogin`
 * (`ScreenName?param=value&...`). Skips nested objects in params.
 */
function serializeRouteToReturnPath(route: {
  name: string
  params?: Record<string, unknown> | object | undefined
}): string {
  const screenName = route.name
  const routeParams = route.params
  if (!routeParams || typeof routeParams !== 'object') {
    return screenName
  }
  const query = new URLSearchParams()
  for (const [paramKey, paramValue] of Object.entries(
    routeParams as Record<string, unknown>
  )) {
    if (paramValue === undefined || paramValue === null) {
      continue
    }
    if (typeof paramValue === 'object') {
      continue
    }
    query.set(paramKey, String(paramValue))
  }
  const queryString = query.toString()
  return queryString ? `${screenName}?${queryString}` : screenName
}

/**
 * Builds the `returnPath` passed to `LoginMobileNumber` so `goAfterLogin` can
 * restore the screen the user was on (same helper used from headers and modals).
 */
export function getReturnPath(navigation: RootNav): string | undefined {
  const leaf = getFocusedLeafRoute(navigation.getState())
  if (!leaf?.name) {
    return undefined
  }
  return serializeRouteToReturnPath(leaf)
}
